<template>
  <audio ref="refAudioManual" playsinline preload="auto" />
  <audio ref="refAudioAuto" playsinline preload="auto" />
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch, computed } from 'vue';

import { useAudioPlayer } from '@/composables/shared/useAudioPlayer';
import { debugLog } from '@/static/develop.js';
import { useAudioStore } from '@/stores/audio';

//수동재생(manualAudio) 오디오 태그, 자동재생(auto) 오디오 태그 2개로 분리 컨트롤 함
const audioStore = useAudioStore();

const refAudioManual = ref<HTMLAudioElement | null>(null);
const refAudioAuto = ref<HTMLAudioElement | null>(null);

const isIdleAudioPlayer = computed(() => {
  return audioStore.playState === 0 && !isPlayingAuto.value;
});

const {
  playCallLogId,
  manualWaitingSrcs,
  autoRetryCount,
  isPlayingAuto,
  isPlayingManual,
  playAudioManual,
  playNextManualAudio,
  playAudioAuto,
  shiftAutoAudio,
  checkPlayState,
  unlockAutoPlay,
} = useAudioPlayer({ refAudioManual, refAudioAuto });

const onManualCanPlay = () => {
  if (isPlayingAuto.value) {
    refAudioAuto.value?.pause();
    shiftAutoAudio();
    isPlayingAuto.value = false;
  }

  refAudioManual.value!.volume = audioStore.playVolume;
  refAudioManual.value!.play();
  checkPlayState(new URL(refAudioManual.value!.src), true);
  audioStore.audioDownloading = null;
};

const onManualEnded = () => {
  isPlayingManual.value = false;
  playNextManualAudio();
};

const onManualPlaying = () => {
  refAudioAuto.value?.pause();
  isPlayingManual.value = true;
  isPlayingAuto.value = false;
};

const onManualError = (event: Event) => {
  if (manualWaitingSrcs.value.length !== 0) {
    playNextManualAudio();
  } else if (isPlayingAuto.value) {
    playAudioAuto();
  }
  audioStore.audioDownloading = null;
};

const onAutoCanPlayThrough = () => {
  debugLog('---audio play --- auto !play!');
  autoRetryCount.value = 0;
  if (audioStore.isSoundOn) {
    refAudioAuto.value!.play().catch((err) => console.warn('[AUTO] play() failed', err));
  }

  const src = refAudioAuto.value?.src;
  if (src) {
    checkPlayState(new URL(src), true);
  }
};

const onAutoPause = () => {
  debugLog('---audio play --- pause');
  if (audioStore.playState > 0) {
    audioStore.setPlayState(0, playCallLogId.value);
  }
};

const onAutoProgress = () => {
  debugLog('---audio play --- progress');
};

const onAutoEnded = () => {
  isPlayingAuto.value = false;
  audioStore.setPlayState(0, '');
  shiftAutoAudio();
  playAudioAuto();
};

const onAutoError = () => {
  isPlayingAuto.value = false;
  autoRetryCount.value += 1;
  shiftAutoAudio();
  playAudioAuto();
};

onMounted(() => {
  //수동 재생 event listenter
  audioStore.playlistAutoFinished = [];
  audioStore.playlistAutoQueue = [];
  refAudioManual.value?.addEventListener('canplay', onManualCanPlay);
  refAudioManual.value?.addEventListener('playing', onManualPlaying);
  refAudioManual.value?.addEventListener('canplaythrough', onManualPlaying);
  refAudioManual.value?.addEventListener('progress', onManualPlaying);
  refAudioManual.value?.addEventListener('ended', onManualEnded);
  refAudioManual.value?.addEventListener('error', onManualError);

  //자동 재생 event listenter
  //주의. 서버에 파일 접근 or load 실패 케이스 있음 (재시도로 처리함)
  refAudioAuto.value?.addEventListener('canplaythrough', onAutoCanPlayThrough);
  refAudioAuto.value?.addEventListener('pause', onAutoPause);
  refAudioAuto.value?.addEventListener('progress', onAutoProgress);
  refAudioAuto.value?.addEventListener('ended', onAutoEnded);
  refAudioAuto.value?.addEventListener('error', onAutoError);

  window.addEventListener('unlock-audio', unlockAutoPlay);
});

onUnmounted(() => {
  // 수동 재생 event listener 제거
  refAudioManual.value?.removeEventListener('canplay', onManualCanPlay);
  refAudioManual.value?.removeEventListener('playing', onManualPlaying);
  refAudioManual.value?.removeEventListener('ended', onManualEnded);
  refAudioManual.value?.removeEventListener('error', onManualError);

  // 자동 재생 event listener 제거
  refAudioAuto.value?.removeEventListener('canplaythrough', onAutoCanPlayThrough);
  refAudioAuto.value?.removeEventListener('pause', onAutoPause);
  refAudioAuto.value?.removeEventListener('progress', onAutoProgress);
  refAudioAuto.value?.removeEventListener('ended', onAutoEnded);
  refAudioAuto.value?.removeEventListener('error', onAutoError);

  window.removeEventListener('unlock-audio', unlockAutoPlay);
});

//오디오 소스 변경 감지 후, 컴포넌트 전달
watch(
  () => audioStore.playlistAutoQueue.length,
  (len) => {
    if (len > 0 && isIdleAudioPlayer.value) {
      playAudioAuto();
    }
  }
);

watch(
  () => audioStore.manualAudioKeys,
  (newKeys) => {
    if (newKeys && newKeys.length > 0) {
      if (refAudioAuto.value && !refAudioAuto.value.paused) {
        refAudioAuto.value.pause();
        shiftAutoAudio();
      }
      audioStore.playlistAutoQueue = []; //재생중인게 있다면 초기화
      playAudioManual(newKeys);
    }
  },
  { deep: true, immediate: false }
);

watch(
  () => audioStore.playState,
  (newVal) => {
    if (newVal === 0) {
      // 수동 오디오 정지
      isPlayingManual.value = false;
      manualWaitingSrcs.value.length = 0;
      refAudioManual.value?.pause();

      // 사운드 꺼짐 상태면 자동 대기열도 초기화
      if (!audioStore.isSoundOn) {
        audioStore.playlistAutoQueue = [];
      }

      // 자동 대기열이 비어 있지 않으면 재생 시도
      if (audioStore.playlistAutoQueue.length > 0) {
        // 현재 자동재생 중이 아닌 경우에만 재생 시도
        playAudioAuto();
      } else {
        refAudioAuto.value?.pause();
      }
    }
  }
);
</script>
